import {
  IntegrationsContainerSliceDefaultPrimaryCategoriesItem,
  IntegrationsContainerSliceV2PrimaryCategoriesItem,
} from 'prismicio-types'
import {useState, useRef, useEffect} from 'react'
import {Icon} from 'src/common/Icon'
import cn from 'classnames'
import {EVENTS, track, TrackingProps} from 'src/common/helpers'

export const CustomSelect = ({
  options,
  activeIndex = 0,
  setActiveIndex,
  v2 = false,
  trackingData,
}: {
  options:
    | IntegrationsContainerSliceDefaultPrimaryCategoriesItem[]
    | IntegrationsContainerSliceV2PrimaryCategoriesItem[]
    | Array<{category_name: string; value: string}>
  activeIndex?: number
  setActiveIndex: (index: number) => void
  v2?: boolean
  trackingData: Omit<TrackingProps, 'location' | 'event'>
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [growUp, setGrowUp] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Decide where should the dropdown grow
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownHeight = Math.min(options.length * 40, 224)
      setGrowUp(spaceBelow < dropdownHeight + 16)
    }
  }, [isOpen, options.length])

  const handleSelectOption = (index: number) => {
    setActiveIndex(index)
    setIsOpen(false)
  }

  return (
    <div className="w-full">
      <div className={`relative h-full ${v2 ? 'w-full' : 'w-[120px] max-w-[120px]'}`}>
        <button
          ref={buttonRef}
          type="button"
          className={cn(
            'relative h-full w-full cursor-default rounded-[4px] border bg-moss-white pr-10 text-left text-moss-black focus:outline focus:outline-1 focus:outline-[#94CC32] sm:leading-6',
            {
              'text-body-m border-grey-150 shadow-sm pl-3 py-1.5': !v2,
              'text-tg-body-m border-black/[10%] pl-4 h-9 py-[7px]': v2,
            },
          )}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          onBlur={() => setIsOpen(false)}
        >
          <span className="flex items-center">
            <span className="block truncate">{options[activeIndex]?.category_name}</span>
          </span>
          <span
            className={cn('pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center', {
              'pr-2': !v2,
              'pr-4': v2,
            })}
          >
            <Icon
              name="chevron-v2"
              className={cn('scale-y-flip text-moss-black transition-all duration-300', {
                'rotate-180': isOpen,
                'h-5 w-5': !v2,
                'h-1.5 w-2.5': v2,
              })}
            />
          </span>
        </button>

        <ul
          className={cn(
            `absolute ${
              growUp ? 'bottom-full top-auto mb-2' : 'top-full mt-1'
            } right-0 z-10 w-full overflow-auto rounded-lg bg-moss-white py-1 shadow-lg ring-1 ring-grey-150 transition-all duration-300 focus:outline-none`,
            {
              'max-h-0 opacity-0': !isOpen,
              'max-h-56 opacity-100': isOpen,
              'text-body-m': !v2,
              'text-tg-body-m': v2,
            },
          )}
          style={{visibility: isOpen ? 'visible' : 'hidden'}}
          tabIndex={-1}
          role="listbox"
          aria-labelledby="listbox-label"
          aria-activedescendant="listbox-option-3"
        >
          {options.map((option, index) => (
            <li
              className={cn('relative cursor-default select-none px-3 py-2 text-grey-550', {
                'bg-grey-120': activeIndex === index,
                'bg-moss-white hover:bg-grey-110': activeIndex !== index,
              })}
              onClick={() => {
                track({
                  ...trackingData,
                  event: EVENTS.CLICK,
                  location: `category-${option.category_name}-${index}`,
                })

                handleSelectOption(index)
              }}
              id="listbox-option-0"
              role="option"
              key={index}
            >
              <div className="flex items-center">
                <span className="block truncate font-normal">{option.category_name}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
